import { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";

import { dispatch, getState } from "../../../state/store";
import { resetDeviceChangeHistory, setCertificateUrl, setDeviceToEdit } from "../../../state/ducks";
import { selectDeviceByDusn } from "../../../state/selectors";

import { alignItems, strCode } from "../../../consts";
import { deviceModalSections } from "./consts/deviceModalSections";
import { t } from "../../../services";
import { deviceGroupUtils, objectUtil } from "../../../utils";
import { formatUtils } from "./utils/formatUtils";
import { groupUtils } from "./utils/groupUtils";

import {
  Checkbox,
  DiscardModal,
  FlexContainers,
  Heading4,
  IconButton,
  Modal,
  PageSelector,
  SmallButton,
  SVG,
  WrapperContainer
} from "../../../components";
import { NewDeviceForm } from "./components/NewDeviceForm/NewDeviceForm";
import { DeviceCertificates } from "./components/DeviceCertificates/DeviceCertificates";
import { DeviceGroups } from "./components/DeviceGroups/DeviceGroups";
import { ButtonsWrapper, RemoveButton } from "./components/NewDeviceForm/styled";
import { CheckboxLabel } from "../../../components/Checkbox/styled";
import { NewDeviceModalContainer, PageSelectorWrapper, SectionContentWrapper } from "./styled";

const deviceModalPages = [
  { id: deviceModalSections.INFO, title: strCode.INFORMATION_TITLE },
  { id: deviceModalSections.CERTIFICATES, title: strCode.CERTIFICATES_TITLE }
];

const NewDeviceModal = ({
  deviceData,
  deviceGroups,
  certificateUrl,
  hasCertificate,
  deviceChangeHistory,
  active,
  createDeviceLoading,
  resetDeviceLoading,
  deleteDeviceLoading,
  certificatesLoading,
  certificateInfoLoading,
  deviceChangeHistoryLoading,
  deviceGroupsLoading,
  deviceGroupChangesLoading,
  deviceChangeHistoryRefresh,
  error,
  setActive,
  onSave,
  onDelete,
  onReset,
  onFetchCertificateInfo,
  onGenerateCertificates,
  onDownloadCertificates,
  onFetchDeviceChangeHistory,
  onCreateDeviceGroup,
  onUpdateDeviceGroup,
  onDeleteDeviceGroup,
  onUpdateDeviceGroupList,
  onDeleteDeviceGroupList
}) => {
  const [originalData, setOriginalData] = useState();
  const [isSameData, setIsSameData] = useState();
  const [savedDeviceData, setSavedDeviceData] = useState();

  const [shouldGenerateCert, setShouldGenerateCert] = useState(false);
  const [isMercuryDeviceSelected, setIsMercuryDeviceSelected] = useState(false);

  const [discardModalActive, setDiscardModalActive] = useState(false);
  const [removeDeviceModalActive, setRemoveDeviceModalActive] = useState(false);
  const [hardResetModalActive, setHardResetModalActive] = useState(false);
  const [revokeModalActive, setRevokeModalActive] = useState(false);

  const [isSubmitted, setIsSubmitted] = useState(false);

  const [activeSection, setActiveSection] = useState(deviceModalSections.INFO);

  const isEdit = useMemo(() => {
    return !!deviceData;
  }, [deviceData]);

  const isManageGroups = useMemo(() => {
    return activeSection === deviceModalSections.DEVICE_GROUPS;
  }, [activeSection]);

  const isMercuryDevice = useMemo(() => {
    return deviceData?.isMercuryDevice;
  }, [deviceData]);

  const isLoading = useMemo(() => {
    return createDeviceLoading || resetDeviceLoading || deleteDeviceLoading;
  }, [createDeviceLoading, resetDeviceLoading, deleteDeviceLoading]);

  const groupOptions = useMemo(() => {
    return deviceGroups?.map((group) => {
      return { id: group?.id, value: group?.name };
    });
  }, [deviceGroups]);

  const deviceFormRef = useRef({
    resetFormData: () => {},
    submit: () => {}
  });

  useEffect(() => {
    if (isSubmitted && !isLoading && !error && active) {
      onDiscard();
    }
  }, [isSubmitted, isLoading, error, active]);

  useEffect(() => {
    if (deviceData) {
      const parsedData = formatUtils.parseDeviceData(deviceData);

      setOriginalData(parsedData);
    } else {
      setOriginalData(null);
      setSavedDeviceData(null);
    }
  }, [deviceData]);

  useEffect(() => {
    if (isEdit && isMercuryDevice) {
      onFetchCertificateInfo(deviceData?.dusn);
      onFetchDeviceChangeHistory(deviceData?.dusn);
    }
  }, [isEdit, isMercuryDevice]);

  useEffect(() => {
    if (isEdit && isMercuryDevice && deviceChangeHistoryRefresh) {
      onFetchDeviceChangeHistory(deviceData?.dusn, true);
    }
  }, [isEdit, isMercuryDevice, deviceChangeHistoryRefresh]);

  useEffect(() => {
    if (!isMercuryDeviceSelected) {
      setShouldGenerateCert(false);
    }
  }, [isMercuryDeviceSelected]);

  const compareDeviceGroups = (dusn, selectedGroups) => {
    const originalDeviceGroups = deviceGroupUtils.getDeviceGroupsByDusn(dusn);

    const { groupsToAdd, groupsToCreate, groupsToRemove } = groupUtils.compareDeviceGroups(
      selectedGroups,
      originalDeviceGroups
    );

    return { groupsToAdd, groupsToCreate, groupsToRemove };
  };

  const onDiscard = () => {
    setActive(false);
    setIsSubmitted(false);
    setOriginalData(null);
    setSavedDeviceData(null);
    setActiveSection(deviceModalSections.INFO);

    if (deviceFormRef?.current?.resetFormData) {
      deviceFormRef?.current?.resetFormData();
    }

    setDiscardModalActive(false);
    setRemoveDeviceModalActive(false);
    setHardResetModalActive(false);

    dispatch(setDeviceToEdit(null));
    dispatch(setCertificateUrl(null));
    dispatch(resetDeviceChangeHistory());
  };

  // eslint-disable-next-line no-unused-vars
  const onSaveDevice = async (data) => {
    const formattedDeviceData = formatUtils.formatDeviceData(data);
    const { dusn, selectedGroups } = data ?? {};
    const kitCodentify = !originalData?.kitCodentify ? data?.kitCodentify ?? null : null;
    const relatedDusn = !originalData?.relatedDusn ? data?.relatedDusn ?? null : null;
    const relatedCodentify = selectDeviceByDusn(relatedDusn)(getState())?.codentify ?? null;
    let relatedKitCodentify = relatedCodentify ? kitCodentify ?? originalData?.kitCodentify : null;

    const kitData =
      kitCodentify || relatedKitCodentify
        ? {
            kitCodentify,
            relatedCodentify,
            relatedKitCodentify
          }
        : null;

    const { groupsToAdd, groupsToCreate, groupsToRemove } = compareDeviceGroups(
      dusn,
      selectedGroups
    );

    const groupPromises = [];
    let createdGroupIds = [];

    if (groupsToCreate?.length) {
      const groups = groupsToCreate?.map((el) => el.id);

      createdGroupIds = await onCreateDeviceGroup(groups);
    }

    if (groupsToAdd?.length || createdGroupIds?.length) {
      const groupIds = groupsToAdd?.map((el) => el.id);
      groupPromises.push(onUpdateDeviceGroupList(dusn, [...groupIds, ...createdGroupIds]));
    }

    if (groupsToRemove?.length) {
      const groupIds = groupsToRemove?.map((el) => el.id);
      groupPromises.push(onDeleteDeviceGroupList(dusn, groupIds));
    }

    const isSuccess = await onSave(formattedDeviceData, kitData);
    await Promise.all(groupPromises);

    if (isSuccess && shouldGenerateCert) {
      onGenerateNewDeviceCertificates(formattedDeviceData.dusn);
    }

    setIsSubmitted(true);
  };

  const onRemoveDevice = async () => {
    await onDelete(deviceData.dusn);
    setRemoveDeviceModalActive(false);
    setIsSubmitted(true);
  };

  const onResetDevice = async () => {
    await onReset(deviceData.dusn);
    setHardResetModalActive(false);
  };

  const onGenerateNewDeviceCertificates = async (dusn) => {
    const certificateUrl = await onGenerateCertificates([dusn], { override: false });

    if (certificateUrl) {
      await onDownloadCertificates([dusn], certificateUrl);
    }
  };

  const onSubmit = () => {
    if (deviceFormRef?.current) {
      deviceFormRef.current.submit();
    }
  };

  const onClose = () => {
    let isDefault;
    if (!originalData) {
      const data = deviceFormRef.current.getDeviceData();

      isDefault = objectUtil.isNullOrDefault(data);
    } else {
      isDefault = isSameData;
    }

    isDefault = isDefault || activeSection === deviceModalSections.CERTIFICATES;
    isDefault ? onDiscard() : setDiscardModalActive(true);
  };

  const handleGenerateCertificates = () => {
    if (originalData?.dusn) {
      onGenerateCertificates([originalData.dusn], { override: !hasCertificate });
    }
  };

  const handleDownloadCertificates = () => {
    if (originalData?.dusn) {
      onDownloadCertificates([originalData.dusn], certificateUrl);
    }
  };

  const handleRevokeCertificate = () => {
    dispatch(setCertificateUrl(null));
    setRevokeModalActive(false);
  };

  const onChangeActiveSection = (section) => {
    if (section !== deviceModalSections.INFO) {
      const deviceData = deviceFormRef?.current?.getDeviceData();
      setSavedDeviceData(deviceData);
    }

    setActiveSection(section);
  };

  const deviceForm = (
    <NewDeviceForm
      ref={deviceFormRef}
      isEdit={isEdit}
      originalData={originalData}
      deviceData={savedDeviceData ? savedDeviceData : originalData}
      groupOptions={groupOptions}
      loading={createDeviceLoading}
      setIsMercuryDeviceSelected={setIsMercuryDeviceSelected}
      setIsSameData={setIsSameData}
      onConfigureGroups={() => onChangeActiveSection(deviceModalSections.DEVICE_GROUPS)}
      onResetDevice={() => setHardResetModalActive(true)}
      onSaveDevice={onSaveDevice}
    />
  );

  return (
    <>
      <Modal
        id={"new-device-modal"}
        active={active}
        closable={false}
        title={
          !isManageGroups ? t(isEdit ? strCode.ABOUT_DEVICE_TITLE : strCode.NEW_DEVICE_TITLE) : null
        }
        preventOverflow
        showFooterBorder={isEdit}
        Container={NewDeviceModalContainer}
        modalHeader={
          isManageGroups ? (
            <WrapperContainer Container={FlexContainers.FlexRowStart} gap={16} widht={"100%"}>
              <IconButton
                onClick={() => setActiveSection(deviceModalSections.INFO)}
                disabled={deviceGroupsLoading || deviceGroupChangesLoading}>
                <SVG.BackArrowIcon />
              </IconButton>
              <Heading4>{t(strCode.MANAGE_GROUPS_TITLE)}</Heading4>
            </WrapperContainer>
          ) : null
        }
        modalFooter={
          <ButtonsWrapper Container={FlexContainers.FlexRowBetween} gap={8} mt={8} width={"100%"}>
            {isEdit && !isManageGroups && (
              <RemoveButton
                secondary
                onClick={() => setRemoveDeviceModalActive(true)}
                disabled={createDeviceLoading}>
                {t(strCode.REMOVE_DEVICE_BTN)}
              </RemoveButton>
            )}

            <WrapperContainer
              Container={
                isManageGroups || isEdit ? FlexContainers.FlexRowEnd : FlexContainers.FlexRowBetween
              }
              gap={8}
              width={"100%"}>
              {!isEdit && !isManageGroups && (
                <WrapperContainer Container={FlexContainers.FlexRow} gap={8}>
                  <Checkbox
                    id={"generate-cert-checkbox"}
                    checked={shouldGenerateCert}
                    onCheck={() => {
                      setShouldGenerateCert((prev) => !prev);
                    }}
                    disabled={!isMercuryDeviceSelected}
                  />

                  <CheckboxLabel
                    for={"generate-cert-checkbox"}
                    onClick={() => {
                      setShouldGenerateCert((prev) => !prev);
                    }}
                    disabled={!isMercuryDeviceSelected}>
                    {t(strCode.GENERATE_CERTIFICATES)}
                  </CheckboxLabel>
                </WrapperContainer>
              )}

              {isManageGroups ? (
                <SmallButton
                  onClick={() => setActiveSection(deviceModalSections.INFO)}
                  disabled={
                    isSameData ||
                    createDeviceLoading ||
                    deviceGroupsLoading ||
                    deviceGroupChangesLoading
                  }
                  loading={createDeviceLoading}>
                  {t(strCode.RETURN_TO_DEVICE_BTN)}
                </SmallButton>
              ) : (
                <WrapperContainer Container={FlexContainers.FlexRow} gap={8}>
                  <SmallButton secondary onClick={onClose} disabled={createDeviceLoading}>
                    {t(strCode.DISCARD_BTN)}
                  </SmallButton>

                  <SmallButton
                    style={{ width: "187px" }}
                    onClick={onSubmit}
                    disabled={
                      isSameData ||
                      createDeviceLoading ||
                      activeSection === deviceModalSections.CERTIFICATES
                    }
                    loading={createDeviceLoading}>
                    {t(isEdit ? strCode.SAVE_CHANGES_BTN : strCode.SUBMIT_BTN)}
                  </SmallButton>
                </WrapperContainer>
              )}
            </WrapperContainer>
          </ButtonsWrapper>
        }>
        <WrapperContainer
          Container={FlexContainers.FlexColumnStart}
          alignItems={alignItems.start}
          gap={16}>
          {isEdit && isMercuryDevice && activeSection !== deviceModalSections.DEVICE_GROUPS ? (
            <PageSelectorWrapper>
              <PageSelector
                pages={deviceModalPages}
                activePage={activeSection}
                setActivePage={(section) => onChangeActiveSection(section)}
              />
            </PageSelectorWrapper>
          ) : null}

          <SectionContentWrapper>
            {activeSection === deviceModalSections.INFO && deviceForm}

            {activeSection === deviceModalSections.CERTIFICATES && (
              <DeviceCertificates
                hasCertificate={hasCertificate || certificateUrl}
                deviceChangeHistory={deviceChangeHistory}
                loading={certificatesLoading}
                certificateInfoLoading={certificateInfoLoading}
                deviceChangeHistoryLoading={deviceChangeHistoryLoading}
                onGenerateCertificates={handleGenerateCertificates}
                onDownloadCertificates={handleDownloadCertificates}
                onRevokeCertificates={() => setRevokeModalActive(true)}
              />
            )}

            {isManageGroups && (
              <DeviceGroups
                groups={deviceGroups}
                deviceGroupsLoading={deviceGroupsLoading}
                groupChangesLoading={deviceGroupChangesLoading}
                onCreateDeviceGroup={onCreateDeviceGroup}
                onUpdateDeviceGroup={onUpdateDeviceGroup}
                onDeleteDeviceGroup={onDeleteDeviceGroup}
              />
            )}
          </SectionContentWrapper>
        </WrapperContainer>
      </Modal>

      <DiscardModal
        title={
          isEdit ? strCode.DISCARD_DEVICE_EDITION_TITLE : strCode.DISCARD_DEVICE_CREATION_TITLE
        }
        text={isEdit ? strCode.DISCARD_DEVICE_EDITION_TEXT : strCode.DISCARD_DEVICE_CREATION_TEXT}
        active={discardModalActive}
        setActive={setDiscardModalActive}
        onDiscard={onDiscard}
      />

      <DiscardModal
        title={strCode.REMOVE_DEVICE_CONFIRMATION_TITLE}
        text={strCode.REMOVE_DEVICE_CONFIRMATION_TEXT}
        confirmBtnText={strCode.REMOVE_DEVICE_BTN}
        loading={deleteDeviceLoading}
        active={removeDeviceModalActive}
        setActive={setRemoveDeviceModalActive}
        onDiscard={onRemoveDevice}
      />

      <DiscardModal
        title={strCode.HARD_RESET_CONFIRMATION_TITLE}
        text={strCode.HARD_RESET_CONFIRMATION_TEXT}
        confirmBtnText={strCode.HARD_RESET_BTN}
        loading={resetDeviceLoading}
        active={hardResetModalActive}
        setActive={setHardResetModalActive}
        onDiscard={onResetDevice}
      />

      <DiscardModal
        title={strCode.REVOKE_CERTIFICATE_TITLE}
        text={strCode.REVOKE_DEVICE_CERTIFICATE_WARNING_MSG}
        confirmBtnText={strCode.REVOKE_BTN}
        active={revokeModalActive}
        setActive={setRevokeModalActive}
        onDiscard={handleRevokeCertificate}
      />
    </>
  );
};

NewDeviceModal.propTypes = {
  deviceData: PropTypes.shape(),
  deviceGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  certificateUrl: PropTypes.string,
  hasCertificate: PropTypes.bool,
  deviceChangeHistory: PropTypes.arrayOf(PropTypes.object),
  active: PropTypes.bool.isRequired,
  createDeviceLoading: PropTypes.bool,
  resetDeviceLoading: PropTypes.bool,
  deleteDeviceLoading: PropTypes.bool,
  certificatesLoading: PropTypes.bool,
  certificateInfoLoading: PropTypes.bool,
  deviceChangeHistoryLoading: PropTypes.bool,
  deviceGroupsLoading: PropTypes.bool,
  deviceGroupChangesLoading: PropTypes.bool,
  deviceChangeHistoryRefresh: PropTypes.bool,
  error: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onFetchCertificateInfo: PropTypes.func.isRequired,
  onGenerateCertificates: PropTypes.func.isRequired,
  onDownloadCertificates: PropTypes.func.isRequired,
  onFetchDeviceChangeHistory: PropTypes.func.isRequired,
  onCreateDeviceGroup: PropTypes.func.isRequired,
  onUpdateDeviceGroup: PropTypes.func.isRequired,
  onDeleteDeviceGroup: PropTypes.func.isRequired,
  onUpdateDeviceGroupList: PropTypes.func.isRequired,
  onDeleteDeviceGroupList: PropTypes.func.isRequired
};

export default NewDeviceModal;
